import { Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CodeBlock({ code }) {
  return (
    <div className="relative overflow-x-auto rounded-3xl bg-slate-950 p-4 text-slate-200">
      <button
        onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied!"); }}
        className="absolute right-2 top-2 p-1.5 text-slate-400 transition-colors hover:text-white"
      >
        <Copy size={12} />
      </button>
      <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{code}</pre>
    </div>
  );
}

function Endpoint({ method, path, desc, children }) {
  const colors = { POST: "bg-green-100 text-green-800 border-green-300", GET: "bg-blue-100 text-blue-800 border-blue-300" };
  return (
    <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
      <div className="border-b border-border px-5 py-4">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 text-xs font-bold border rounded-sm ${colors[method]}`}>{method}</span>
          <code className="font-mono text-sm">{path}</code>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{desc}</p>
      </div>
      <div className="space-y-4 p-5">{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{title}</p>
      {children}
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div data-testid="api-docs-page" className="space-y-6 sm:space-y-8">
      <div className="rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Developer Docs</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">API Documentation</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Panduan integrasi payment gateway ke website, app, atau backend kamu.</p>
      </div>

      <div className="rounded-3xl border border-border/70 bg-card/90 p-5 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Base URL</h3>
        <code className="font-mono text-sm">{BACKEND_URL}/api</code>
      </div>

      <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
        <div className="border-b border-border px-5 py-4">
          <h3 className="font-heading text-lg font-bold">Authentication</h3>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-3">
            Semua request ke API memerlukan header <code className="font-mono text-xs bg-secondary px-1 py-0.5 rounded-sm">X-API-Key</code>.
          </p>
          <CodeBlock code="X-API-Key: gw_your_api_key_here" />
        </div>
      </div>

      <div className="space-y-6">
        <Endpoint method="POST" path="/api/v1/payment/create" desc="Buat payment baru dan dapatkan QRIS">
          <Section title="Request Body">
            <CodeBlock code={`{\n  "amount": 50000,\n  "description": "Bayar order #123"\n}`} />
          </Section>
          <Section title="Response">
            <CodeBlock code={`{\n  "id": "payment_id",\n  "amount": 50000,\n  "unique_amount": 50042,\n  "status": "pending",\n  "qris_image": "data:image/png;base64,...",\n  "qris_url": "/api/payment/{id}/qris-image",\n  "created_at": "2025-01-01T00:00:00",\n  "expires_at": "2025-01-01T00:10:00"\n}`} />
          </Section>
          <Section title="cURL">
            <CodeBlock code={`curl -X POST ${BACKEND_URL}/api/v1/payment/create \\\n  -H "Content-Type: application/json" \\\n  -H "X-API-Key: gw_your_api_key" \\\n  -d '{"amount": 50000, "description": "Test"}'`} />
          </Section>
        </Endpoint>

        <Endpoint method="GET" path="/api/v1/payment/{id}/status" desc="Cek status pembayaran">
          <Section title="Response">
            <CodeBlock code={`{\n  "id": "payment_id",\n  "status": "paid",\n  "amount": 50000,\n  "unique_amount": 50042,\n  "paid_at": "2025-01-01T00:05:00",\n  "tx_id": "gobiz_tx_id"\n}`} />
          </Section>
          <Section title="cURL">
            <CodeBlock code={`curl ${BACKEND_URL}/api/v1/payment/PAYMENT_ID/status \\\n  -H "X-API-Key: gw_your_api_key"`} />
          </Section>
        </Endpoint>

        <Endpoint method="GET" path="/api/v1/payment/{id}/qris" desc="Download gambar QRIS (PNG)">
          <Section title="cURL">
            <CodeBlock code={`curl ${BACKEND_URL}/api/v1/payment/PAYMENT_ID/qris \\\n  -H "X-API-Key: gw_your_api_key" \\\n  -o qris.png`} />
          </Section>
        </Endpoint>

        <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
          <div className="border-b border-border px-5 py-4">
            <h3 className="font-heading text-lg font-bold">Contoh Integrasi PHP</h3>
            <p className="text-sm text-muted-foreground mt-1">Untuk website PHP apa pun</p>
          </div>
          <div className="p-5">
            <CodeBlock code={`<?php
// ── Config ──
$api_url = "${BACKEND_URL}/api";
$api_key = "gw_your_api_key";

function createPayment($amount, $desc = "") {
    global $api_url, $api_key;
    $ch = curl_init("$api_url/v1/payment/create");
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER => [
            "Content-Type: application/json",
            "X-API-Key: $api_key"
        ],
        CURLOPT_POSTFIELDS => json_encode([
            "amount" => $amount,
            "description" => $desc
        ])
    ]);
    $res = curl_exec($ch);
    curl_close($ch);
    return json_decode($res, true);
}

function checkPayment($id) {
    global $api_url, $api_key;
    $ch = curl_init("$api_url/v1/payment/$id/status");
    curl_setopt_array($ch, [
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER => ["X-API-Key: $api_key"]
    ]);
    $res = curl_exec($ch);
    curl_close($ch);
    return json_decode($res, true);
}

// ── Contoh ──
$payment = createPayment(50000, "Order #123");
echo "Bayar: Rp " . number_format($payment['unique_amount']);
// Tampilkan QR: <img src="<?= $payment['qris_image'] ?>" />

// Polling status:
while (true) {
    sleep(5);
    $s = checkPayment($payment['id']);
    if ($s['status'] === 'paid') {
        echo "BERHASIL! Tx: " . $s['tx_id'];
        break;
    }
    if ($s['status'] === 'expired') {
        echo "Expired";
        break;
    }
}
?>`} />
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
          <div className="border-b border-border px-5 py-4">
            <h3 className="font-heading text-lg font-bold">Contoh Integrasi JavaScript</h3>
          </div>
          <div className="p-5">
            <CodeBlock code={`const API = "${BACKEND_URL}/api";
const KEY = "gw_your_api_key";

async function createPayment(amount, desc = "") {
  const res = await fetch(\`\${API}/v1/payment/create\`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-API-Key": KEY },
    body: JSON.stringify({ amount, description: desc }),
  });
  return res.json();
}

async function checkPayment(id) {
  const res = await fetch(\`\${API}/v1/payment/\${id}/status\`, {
    headers: { "X-API-Key": KEY },
  });
  return res.json();
}

// Contoh:
const p = await createPayment(50000, "Order #123");
document.querySelector("#qr").src = p.qris_image;

const poll = setInterval(async () => {
  const s = await checkPayment(p.id);
  if (s.status === "paid") {
    clearInterval(poll);
    alert("Pembayaran berhasil!");
  }
}, 5000);`} />
          </div>
        </div>
      </div>
    </div>
  );
}
